import {ApolloClient, ApolloQueryResult, gql} from '@apollo/client';
import {errorResponse, loadingResponse, NetworkRequestStatus, NetworkResult} from './NetworkResult';
import {DownloadedIndoorWorkout} from '../Models/DownloadedIndoorWorkout';
import {Power} from '../Domain/Power';
import {Duration} from '../Domain/Duration';
import {StrokeRate} from '../Domain/StrokeRate';
import {Speed} from '../Domain/Speed';
import {Distance} from '../Domain/Distance';
import {Energy} from '../Domain/Energy';
import {Force} from '../Domain/Force';
import {Fraction} from '../Domain/Fraction';
import {HeartRate} from '../Domain/HeartRate';
import {CacheStore} from './Cache/Cache';

export class StrokesService {
    constructor(private client: ApolloClient<any>, private cacheStore: CacheStore) {}

    public async forWorkoutId(workoutId: number): Promise<NetworkResult<DownloadedIndoorWorkout>> {
        try {
            let response: NetworkResult<DownloadedIndoorWorkout> = loadingResponse();
            do {
                response = await this.query(workoutId);
                if (response.status === NetworkRequestStatus.Loading) {
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                }
            } while (response.status === NetworkRequestStatus.Loading);

            return response;
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async forToken(workoutId: number, token: string) {
        try {
            let response: NetworkResult<DownloadedIndoorWorkout> = loadingResponse();
            do {
                response = await this.queryWithToken(workoutId, token);
                if (response.status === NetworkRequestStatus.Loading) {
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                }
            } while (response.status === NetworkRequestStatus.Loading);

            return response;
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private async query(workoutId: number): Promise<NetworkResult<DownloadedIndoorWorkout>> {
        try {
            const existing = await this.cacheStore.retrieve('' + workoutId);
            if (existing) {
                return {
                    status: NetworkRequestStatus.Success,
                    data: this.fromResponse(existing),
                };
            }
        } catch (e) {
            console.error(e);
            //Continue
        }
        try {
            const apolloQueryResult: ApolloQueryResult<any> = await this.client.query({
                query: gql`
                    query ($workoutId: Int!) {
                        download(type: json, workout_id: $workoutId, auth: ajax) {
                            id
                            status
                            type
                            link
                        }
                    }
                `,
                variables: {
                    workoutId,
                },
                fetchPolicy: 'network-only',
            });

            const raw = await this.processResult(workoutId, apolloQueryResult);
            if (raw.status === NetworkRequestStatus.Success) {
                return {
                    status: raw.status,
                    data: this.fromResponse(raw.data),
                };
            }
            return raw;
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private async queryWithToken(workoutId: number, token: string): Promise<NetworkResult<DownloadedIndoorWorkout>> {
        try {
            const existing = await this.cacheStore.retrieve('' + workoutId);
            if (existing) {
                return {
                    status: NetworkRequestStatus.Success,
                    data: this.fromResponse(existing),
                };
            }
        } catch (e) {
            console.error(e);
            //Continue
        }
        try {
            const apolloQueryResult: ApolloQueryResult<any> = await this.client.query({
                query: gql`
                    query ($workoutId: Int!, $token: String) {
                        download(type: json, workout_id: $workoutId, auth: token, token: $token) {
                            id
                            status
                            type
                            link
                        }
                    }
                `,
                variables: {
                    workoutId,
                    token,
                },
                fetchPolicy: 'network-only',
            });

            const raw = await this.processResult(workoutId, apolloQueryResult);
            if (raw.status === NetworkRequestStatus.Success) {
                return {
                    status: raw.status,
                    data: this.fromResponse(raw.data),
                };
            }
            return raw;
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private async processResult(
        workoutId: number,
        apolloQueryResult: ApolloQueryResult<any>,
    ): Promise<NetworkResult<any>> {
        try {
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            if (apolloQueryResult.data.download.status === 'preparing') {
                return {
                    status: NetworkRequestStatus.Loading,
                };
            }

            const response = await fetch(apolloQueryResult.data.download.link);
            if (!response.ok) {
                return errorResponse();
            }
            const data = await response.json();
            await this.cacheStore.store('' + workoutId, data);
            return {
                status: NetworkRequestStatus.Success,
                data: data,
            };
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private fromResponse(json: any): DownloadedIndoorWorkout {
        return {
            id: json.id,
            workoutIntervals: json.workout_intervals.map((workoutInterval: any) => {
                return {
                    id: workoutInterval.id,
                    strokes: workoutInterval.strokes.map((stroke: any) => {
                        return {
                            strokeNumber: stroke.stroke_number,
                            power: stroke.power === null ? null : Power.fromWatts(stroke.power),
                            totalTime: stroke.time === null ? null : Duration.fromSeconds(stroke.time),
                            totalDistance: stroke.distance === null ? null : Distance.fromMeters(stroke.distance),
                            distancePerStroke:
                                stroke.distance_per_stroke === null
                                    ? null
                                    : Distance.fromCentimeters(stroke.distance_per_stroke),
                            strokeRate:
                                stroke.stroke_rate === null
                                    ? null
                                    : StrokeRate.fromStrokesPerMinute(stroke.stroke_rate),
                            speed:
                                stroke.estimated_500m_time === null
                                    ? null
                                    : Speed.fromDurationPer500Meters(Duration.fromSeconds(stroke.estimated_500m_time)),
                            strokeLength:
                                stroke.stroke_length === null ? null : Distance.fromCentimeters(stroke.stroke_length),
                            energy:
                                stroke.energy_per_stroke === null ? null : Energy.fromJoules(stroke.energy_per_stroke),
                            peakForce: stroke.peak_force === null ? null : Force.fromNewton(stroke.peak_force),
                            peakForcePosition:
                                stroke.peak_force_pos === null ? null : Distance.fromCentimeters(stroke.peak_force_pos),
                            relativePeakForcePosition:
                                stroke.rel_peak_force_pos === null
                                    ? null
                                    : Fraction.fromValue(stroke.rel_peak_force_pos),
                            driveTime: stroke.drive_time === null ? null : Duration.fromSeconds(stroke.drive_time),
                            recoverTime:
                                stroke.recover_time === null ? null : Duration.fromSeconds(stroke.recover_time),
                            heartRate: stroke.pulse ? HeartRate.fromBpm(stroke.pulse) : null,
                            curve:
                                stroke.curve_data === null
                                    ? null
                                    : stroke.curve_data
                                          .split(',')
                                          .map((value: any) => parseInt(value, 10))
                                          .filter((value: number) => value !== -32768)
                                          .map((value: number, index: number) => {
                                              return {
                                                  x: (index + 1) * 2.2, //Each increment = 2.2cm
                                                  y: value,
                                              };
                                          }),
                        };
                    }),
                };
            }),
        };
    }
}
